import type { MouseEvent } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import logo from '../../../assets/logo.svg';
import { paths } from '@/constants';
import { LanguageSwitch } from './LanguageSwitch';
import { UserMenu } from './UserMenu';

interface HeaderProps {
  showLandingNavigation?: boolean;
}

const LANDING_LINKS = [
  { href: '#why', labelKey: 'why' },
  { href: '#features', labelKey: 'features' },
  { href: '#services', labelKey: 'services' },
  { href: '#pricing', labelKey: 'pricing' },
] as const;

export function Header({ showLandingNavigation = false }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { locale?: string };
  const locale = params.locale || i18n.resolvedLanguage || i18n.language || 'en';

  const handleLogoClick = () => {
    navigate({ to: paths.home, params: { locale } });
  };

  const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const hash = event.currentTarget.hash;
    const target = document.querySelector(hash);

    if (!target) {
      event.preventDefault();
      navigate({
        to: paths.home,
        params: { locale },
        hash: hash.replace(/^#/, ''),
      });
      return;
    }

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState({}, '', hash);
  };

  return (
    <header className='relative z-20 w-full px-4 py-4 md:px-6 lg:px-8 bg-white/60 backdrop-blur-md border-b border-white/20 shadow-sm'>
      <div className='relative flex items-center justify-between'>
        {showLandingNavigation ? (
          <button onClick={handleLogoClick} className='block'>
            <img src={logo} alt='Hopsa' className='h-8 w-auto md:h-10' />
          </button>
        ) : (
          <div className='flex items-center gap-1'>
            <LanguageSwitch />
          </div>
        )}

        {showLandingNavigation ? (
          <nav
            aria-label={t('landing.navigation.ariaLabel')}
            className='absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 lg:flex'
          >
            {LANDING_LINKS.map(({ href, labelKey }) => (
              <a
                key={href}
                href={href}
                onClick={handleAnchorClick}
                className='whitespace-nowrap rounded-full px-3 py-2 text-sm font-bold !text-[#6b6573] transition-colors hover:!text-[#16131a] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
              >
                {t(`landing.navigation.${labelKey}`)}
              </a>
            ))}
            <a
              href='#landing-start'
              onClick={handleAnchorClick}
              className='ml-2 whitespace-nowrap rounded-full border border-primary bg-primary px-4 py-2 text-sm font-extrabold !text-primary-foreground shadow-sm transition-colors hover:border-primary/90 hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
            >
              {t('landing.navigation.vibe')}
            </a>
          </nav>
        ) : (
          <div className='absolute left-1/2 -translate-x-1/2'>
            <button onClick={handleLogoClick} className='block'>
              <img src={logo} alt='Hopsa' className='h-8 w-auto md:h-10' />
            </button>
          </div>
        )}

        <div className='flex items-center gap-1'>
          {showLandingNavigation ? <LanguageSwitch /> : null}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
